import { pushUndo, showUndoToast } from '@aurora/platform'
import { auroraStore } from '../store/auroraStore'
import { currentPlan, type PlanBlock } from '../store/selectors'

/**
 * Every write to the plan goes through here.
 *
 * The plan is a body inside an artifact, not a table, so editing it means
 * rewriting that body. Doing that in each component would mean five copies of
 * "find the day, find the block, keep the rest intact" — and five places for
 * the shapes to drift apart. One writer also means one place to make every
 * plan change undoable, which is why they all are.
 */

type PlanDataDraft = any

function planArtifactId(): string | undefined {
  return (currentPlan(auroraStore.getState() as any) as any)?._id
}

/**
 * Apply `mutate` to a copy of the plan body and save it.
 *
 * Returns false when there is no plan to edit, so callers can stay silent
 * rather than pretending something happened.
 */
export function editPlan(label: string, mutate: (data: PlanDataDraft) => void): boolean {
  const s = auroraStore.getState() as any
  const plan: any = currentPlan(s)
  if (!plan) return false

  const before = structuredClone(plan.data ?? {})
  const after = structuredClone(plan.data ?? {})
  mutate(after)

  const write = (data: PlanDataDraft) =>
    (auroraStore.getState() as any).saveArtifactEdit(plan._id, { data })

  write(after)
  pushUndo({ label, undo: () => write(before), redo: () => write(after) })
  showUndoToast(label)
  return true
}

/** Every day in the plan, whichever shape it uses, as live references. */
function allDays(data: PlanDataDraft): any[] {
  if (Array.isArray(data.weeks)) {
    return data.weeks.flatMap((w: any) => (Array.isArray(w?.days) ? w.days : []))
  }
  return Array.isArray(data.days) ? data.days : []
}

/**
 * The day's entry, created if absent.
 *
 * A parent can tick a ritual on a day the plan never mentioned; refusing that
 * because the shape has no slot for it would be the app arguing with real life.
 */
function findDay(data: PlanDataDraft, date: string, create = false): any | undefined {
  const existing = allDays(data).find((d) => d?.date === date)
  if (existing || !create) return existing

  const day = { date, blocks: [] as any[] }
  if (Array.isArray(data.weeks) && data.weeks.length) {
    const week =
      data.weeks.find((w: any) => (w?.days ?? []).some((d: any) => (d?.date ?? '') <= date)) ??
      data.weeks[data.weeks.length - 1]
    week.days = [...(week.days ?? []), day].sort((a: any, b: any) =>
      (a.date ?? '').localeCompare(b.date ?? ''),
    )
  } else {
    data.days = [...(data.days ?? []), day].sort((a: any, b: any) =>
      (a.date ?? '').localeCompare(b.date ?? ''),
    )
  }
  return day
}

function findBlock(data: PlanDataDraft, date: string, blockId: string): any | undefined {
  return (findDay(data, date)?.blocks ?? []).find((b: any) => b?.id === blockId)
}

// ── blocks ───────────────────────────────────────────────────

export function setBlockStatus(
  date: string,
  blockId: string,
  status: 'pending' | 'done' | 'partial' | 'skipped',
  title?: string,
): boolean {
  const label =
    status === 'done'
      ? `Marked "${title ?? 'it'}" done`
      : status === 'skipped'
        ? `Skipped "${title ?? 'it'}"`
        : status === 'partial'
          ? `Marked "${title ?? 'it'}" partly done`
          : `Reopened "${title ?? 'it'}"`

  // Spine first: when the block is a synced row, its status is a field write —
  // granular, offline-safe, and untouched by later plan revisions.
  const s = auroraStore.getState() as any
  const spineBlock = s.blocks?.[blockId]
  if (spineBlock) {
    const before = spineBlock.status ?? 'pending'
    s.setBlockStatus(blockId, status)
    pushUndo({
      label,
      undo: () => (auroraStore.getState() as any).setBlockStatus(blockId, before),
      redo: () => (auroraStore.getState() as any).setBlockStatus(blockId, status),
    })
    showUndoToast(label)
    return true
  }

  return editPlan(label, (data) => {
    const block = findBlock(data, date, blockId)
    if (!block) return
    block.status = status
    block.completedAt = status === 'pending' ? undefined : Date.now()
  })
}

export function updateBlock(date: string, blockId: string, patch: Partial<PlanBlock>): boolean {
  return editPlan(`Edited "${patch.title ?? 'the activity'}"`, (data) => {
    const block = findBlock(data, date, blockId)
    if (block) Object.assign(block, patch)
  })
}

export function addBlock(date: string, block: Partial<PlanBlock>): boolean {
  return editPlan(`Added "${block.title ?? 'an activity'}"`, (data) => {
    const day = findDay(data, date, true)
    if (!day) return
    day.blocks = [
      ...(day.blocks ?? []),
      { id: `b_${Math.random().toString(36).slice(2, 9)}`, status: 'pending', minutes: 10, ...block },
    ]
  })
}

export function removeBlock(date: string, blockId: string, title?: string): boolean {
  return editPlan(`Removed "${title ?? 'the activity'}"`, (data) => {
    const day = findDay(data, date)
    if (day) day.blocks = (day.blocks ?? []).filter((b: any) => b?.id !== blockId)
  })
}

/** Reorder within the day. `by` is -1 for earlier, 1 for later. */
export function moveBlock(date: string, blockId: string, by: -1 | 1): boolean {
  return editPlan('Reordered the day', (data) => {
    const day = findDay(data, date)
    const blocks: any[] = day?.blocks ?? []
    const i = blocks.findIndex((b) => b?.id === blockId)
    const j = i + by
    if (i < 0 || j < 0 || j >= blocks.length) return
    ;[blocks[i], blocks[j]] = [blocks[j], blocks[i]]
  })
}

/** Move a block to another day — how a missed morning gets rescued. */
export function moveBlockToDate(from: string, blockId: string, to: string): boolean {
  return editPlan('Moved to another day', (data) => {
    const fromDay = findDay(data, from)
    const block = (fromDay?.blocks ?? []).find((b: any) => b?.id === blockId)
    if (!fromDay || !block) return
    fromDay.blocks = fromDay.blocks.filter((b: any) => b?.id !== blockId)
    const toDay = findDay(data, to, true)
    if (toDay) toDay.blocks = [...(toDay.blocks ?? []), block]
  })
}

// ── rituals & notes ──────────────────────────────────────────

export function toggleRitual(date: string, key: string, title: string, done: boolean): boolean {
  return editPlan(done ? `${title}: done` : `${title}: unchecked`, (data) => {
    const day = findDay(data, date, true)
    if (!day) return
    const current: string[] = Array.isArray(day.ritualsDone) ? day.ritualsDone : []
    day.ritualsDone = done ? [...new Set([...current, key])] : current.filter((k) => k !== key)
  })
}

export function setDayNote(date: string, note: string): boolean {
  return editPlan('Saved the note', (data) => {
    const day = findDay(data, date, true)
    if (day) day.note = note
  })
}

export function setWeekTheme(weekNumber: number, theme: string): boolean {
  return editPlan('Renamed the week', (data) => {
    const week = (data.weeks ?? []).find((w: any, i: number) => (w?.number ?? i + 1) === weekNumber)
    if (week) week.theme = theme
  })
}
